// ABOUTME: A listing's text read into blocks for ListingText: either the HTML the operator's rich text editor stores
// ABOUTME: (p, br, lists, bold, http(s) links) or the small markdown dialect (a bold line is a heading, "- " a bullet).

/** A run of a block's text; with an href it is a link, and only ever an absolute http(s) one. */
export interface ListingTextPart {
  text: string;
  href?: string;
}

/**
 * `text` is always the plain text. `parts` (for bullets, one list per item) is there only when the text holds a link:
 * the same text cut into runs, the links among them.
 */
export type ListingTextBlock =
  | { kind: 'heading'; text: string; parts?: ListingTextPart[] }
  | {
      kind: 'bullets';
      items: string[];
      ordered?: boolean;
      parts?: ListingTextPart[][];
    }
  | { kind: 'paragraph'; text: string; parts?: ListingTextPart[] };

const HEADING = /^\*\*(.+)\*\*$/;
const BULLET = /^[-*]\s+(.*)$/;

/** The small markdown dialect, line by line. */
const markdownBlocks = (source: string): ListingTextBlock[] =>
  source
    .split('\n')
    .map(line => line.trim())
    .reduce<ListingTextBlock[]>((blocks, line) => {
      const last = blocks[blocks.length - 1];
      if (!line) return [...blocks, { kind: 'paragraph', text: '' }];
      const heading = HEADING.exec(line);
      if (heading) return [...blocks, { kind: 'heading', text: heading[1] }];
      const bullet = BULLET.exec(line);
      if (bullet) {
        if (last?.kind === 'bullets') {
          last.items.push(bullet[1]);
          return blocks;
        }
        return [...blocks, { kind: 'bullets', items: [bullet[1]] }];
      }
      if (last?.kind === 'paragraph' && last.text) {
        last.text = `${last.text}\n${line}`;
        return blocks;
      }
      return [...blocks, { kind: 'paragraph', text: line }];
    }, [])
    .filter(block => block.kind !== 'paragraph' || block.text);

/**
 * A text written as HTML: it opens or closes any tag, or holds a comment. A "<" that starts no tag ("< 5 km") stays
 * markdown. Any tag counts, so markup the editor never writes (a lone <script>) is dropped, never shown as text.
 */
const HTML_TAG = /<\/?[a-z][a-z0-9-]*(\s[^<>]*)?\/?>|<!--/i;

const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'BLOCKQUOTE',
  'UL',
  'OL',
  'LI'
]);
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const BOLD_TAGS = new Set(['STRONG', 'B']);
// Their content is never text for a talent to read.
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'TEMPLATE', 'NOSCRIPT']);

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

/**
 * While the HTML is read, a link travels inside the text as LINK_START href LINK_TEXT text LINK_END, so trimming and
 * joining the text keeps it in place; partsOf cuts it out again. The three are private-use characters, taken out of
 * the operator's own text first.
 */
const LINK_START = '\uE000';
const LINK_TEXT = '\uE001';
const LINK_END = '\uE002';
const MARKERS = /[\uE000-\uE002]/g;
const LINK = /\uE000([^\uE001]*)\uE001([^\uE002]*)\uE002/g;

/** Control characters, which an href written by hand never needs; the URL parser would drop or encode them silently. */
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001F\u007F]/;

/**
 * The href of a link a talent may follow: an absolute http(s) URL. javascript:, data:, mailto:, relative: none, and
 * none with a control character anywhere in it ("java\tscript:", "https://a.de/\n…"), rather than trusting the parser to
 * strip it.
 */
export const safeHref = (raw: string | null): string | undefined => {
  if (!raw || CONTROL.test(raw.trim())) return undefined;
  try {
    const url = new URL(raw.trim());
    // url.href is normalised and percent-encoded, so it never holds one of the markers.
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};

interface Inline {
  text: string;
  /** Only the bold part of the text, to tell a heading written as a bold paragraph. */
  bold: string;
}

/** The visible text of inline content: whitespace collapsed as a browser would, a <br> a line break. */
const inlineOf = (node: Node, inBold = false): Inline => {
  if (node.nodeType === TEXT_NODE) {
    const text = (node.textContent ?? '')
      .replace(MARKERS, '')
      .replace(/[ \t\r\n\f]+/g, ' ');
    return { text, bold: inBold ? text : '' };
  }
  if (node.nodeType !== ELEMENT_NODE) return { text: '', bold: '' };
  const element = node as Element;
  if (SKIPPED_TAGS.has(element.tagName)) return { text: '', bold: '' };
  if (element.tagName === 'BR') return { text: '\n', bold: '' };
  const bold = inBold || BOLD_TAGS.has(element.tagName);
  const inline = Array.from(element.childNodes).reduce<Inline>(
    (all, child) => {
      const part = inlineOf(child, bold);
      return { text: all.text + part.text, bold: all.bold + part.bold };
    },
    { text: '', bold: '' }
  );
  if (element.tagName !== 'A') return inline;
  // A link the talent may follow keeps its href; any other (javascript:, data:, mailto:, empty) stays its text.
  const href = safeHref(element.getAttribute('href'));
  if (!href || !inline.text.trim()) return inline;
  const wrap = (text: string) =>
    `${LINK_START}${href}${LINK_TEXT}${text}${LINK_END}`;
  return {
    text: wrap(inline.text),
    bold: inline.bold ? wrap(inline.bold) : ''
  };
};

/** A text read with its links in it, as the plain text and, when it holds a link, its runs. */
const partsOf = (
  encoded: string
): { text: string; parts?: ListingTextPart[] } => {
  if (!encoded.includes(LINK_START)) return { text: encoded };
  const parts: ListingTextPart[] = [];
  let from = 0;
  encoded.replace(LINK, (match, href: string, text: string, at: number) => {
    if (at > from) parts.push({ text: encoded.slice(from, at) });
    parts.push({ text, href });
    from = at + match.length;
    return match;
  });
  if (from < encoded.length) parts.push({ text: encoded.slice(from) });
  // tidy trimmed each line of the encoded text, but a space just inside a link at a line's edge sits behind a marker:
  // trim it here, at the block's ends and around each line break, so the parts read exactly as the plain text would.
  const trimmed = parts
    .map((part, index) => {
      let text = part.text.replace(/ *\n */g, '\n');
      if (index === 0) text = text.trimStart();
      if (index === parts.length - 1) text = text.trimEnd();
      return { ...part, text };
    })
    .reduce<ListingTextPart[]>((all, part) => {
      const previous = all[all.length - 1];
      // A line ending in a part followed by one starting on the next line: the space before the break goes too.
      if (previous && part.text.startsWith('\n'))
        previous.text = previous.text.trimEnd();
      if (previous && previous.text.endsWith('\n'))
        part.text = part.text.trimStart();
      // Two spaces meeting at a link's edge read as one, as a browser shows them: the one outside the link stays.
      if (previous && /\s$/.test(previous.text) && part.text.startsWith(' '))
        previous.text = previous.text.trimEnd();
      return [...all, part];
    }, [])
    .filter(part => part.text);
  return { text: trimmed.map(part => part.text).join(''), parts: trimmed };
};

/** A block read from the HTML, its links cut out of the text into parts. */
const withLinks = (block: ListingTextBlock): ListingTextBlock => {
  if (block.kind === 'bullets') {
    const items = block.items.map(partsOf);
    const linked = items.some(item => item.parts);
    return {
      ...block,
      items: items.map(item => item.text),
      ...(linked
        ? { parts: items.map(item => item.parts ?? [{ text: item.text }]) }
        : {})
    };
  }
  const { text, parts } = partsOf(block.text);
  return { ...block, text, ...(parts ? { parts } : {}) };
};

/** Each line trimmed, empty lines at the start and end dropped: "<p><br></p>" is no text. */
const tidy = (text: string): string =>
  text
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .trim();

const hasBlockChild = (element: Element): boolean =>
  Array.from(element.children).some(child => BLOCK_TAGS.has(child.tagName));

/** The text of one list item, a nested list's items following it, flattened. */
const itemTexts = (item: Element): string[] => {
  const nested = Array.from(item.children).filter(
    child => child.tagName === 'UL' || child.tagName === 'OL'
  );
  const own = Array.from(item.childNodes).filter(
    child => !nested.includes(child as Element)
  );
  const text = tidy(own.map(child => inlineOf(child).text).join(''));
  return [
    ...(text ? [text] : []),
    ...nested.flatMap(list =>
      Array.from(list.children)
        .filter(child => child.tagName === 'LI')
        .flatMap(itemTexts)
    )
  ];
};

/**
 * A list as blocks. An <ol> is numbered, except that Quill 2 writes every list as <ol> and marks each item with
 * data-list="bullet" or "ordered": consecutive items of one kind make one block, so a list switching kinds keeps both.
 */
const listBlocks = (list: Element): ListingTextBlock[] =>
  Array.from(list.children)
    .filter(child => child.tagName === 'LI')
    .reduce<{ ordered: boolean; items: Element[] }[]>((runs, item) => {
      const ordered =
        list.tagName === 'OL' && item.getAttribute('data-list') !== 'bullet';
      const last = runs[runs.length - 1];
      if (last && last.ordered === ordered) {
        last.items.push(item);
        return runs;
      }
      return [...runs, { ordered, items: [item] }];
    }, [])
    .flatMap(({ ordered, items }): ListingTextBlock[] => {
      const texts = items.flatMap(itemTexts);
      if (!texts.length) return [];
      return [
        { kind: 'bullets', items: texts, ...(ordered ? { ordered } : {}) }
      ];
    });

/** One paragraph-like element: a heading when it is a heading tag or bold from end to end. */
const textBlock = (element: Element): ListingTextBlock[] => {
  const inline = inlineOf(element);
  const text = tidy(inline.text);
  if (!text) return [];
  const allBold = tidy(inline.bold) === text && !text.includes('\n');
  if (HEADING_TAGS.has(element.tagName) || allBold) {
    return [{ kind: 'heading', text }];
  }
  return [{ kind: 'paragraph', text }];
};

/** The blocks of a container's children; loose inline content between blocks becomes a paragraph. */
const htmlBlocks = (container: Element): ListingTextBlock[] => {
  const blocks: ListingTextBlock[] = [];
  let loose = '';
  const flush = () => {
    const text = tidy(loose);
    if (text) blocks.push({ kind: 'paragraph', text });
    loose = '';
  };
  Array.from(container.childNodes).forEach(child => {
    const element = child as Element;
    if (child.nodeType !== ELEMENT_NODE || !BLOCK_TAGS.has(element.tagName)) {
      loose += inlineOf(child).text;
      return;
    }
    flush();
    if (element.tagName === 'UL' || element.tagName === 'OL') {
      blocks.push(...listBlocks(element));
      return;
    }
    if (element.tagName === 'LI') {
      const text = tidy(inlineOf(element).text);
      if (text) blocks.push({ kind: 'bullets', items: [text] });
      return;
    }
    blocks.push(
      ...(hasBlockChild(element) ? htmlBlocks(element) : textBlock(element))
    );
  });
  flush();
  return blocks;
};

/**
 * The HTML read in an inert document (DOMParser runs no script and loads nothing), and only its text kept, plus the
 * href of each http(s) link: nothing else of the markup reaches the page, so no image, handler, style or other link of
 * the operator's HTML ever renders.
 */
const fromHtml = (source: string): ListingTextBlock[] => {
  const document = new DOMParser().parseFromString(source, 'text/html');
  return htmlBlocks(document.body).map(withLinks);
};

/** A character reference: &nbsp;, &amp;, &#160;, &#xA0;. */
const ENTITY = /&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/i;

/** A text without tags, its character references read (in the same inert document); the line breaks stay. */
const decodeEntities = (source: string): string =>
  new DOMParser()
    .parseFromString(
      `<!doctype html><body><textarea>${source.replace(
        /</g,
        '&lt;'
      )}</textarea>`,
      'text/html'
    )
    .querySelector('textarea')!.value;

/**
 * The blocks of a listing text, in order. Empty for no text, also for an editor's empty "<p><br></p>" and a lone
 * "&nbsp;". A text without tags is markdown, its character references read first.
 */
export const toBlocks = (
  source: string | undefined | null
): ListingTextBlock[] => {
  if (!source) return [];
  if (HTML_TAG.test(source)) return fromHtml(source);
  return markdownBlocks(ENTITY.test(source) ? decodeEntities(source) : source);
};

/** Whether a listing text has anything to show: its section is left out otherwise. */
export const hasListingText = (source: string | undefined | null): boolean =>
  toBlocks(source).length > 0;
